import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import ChatIcon from '@mui/icons-material/Chat';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { UploadState, FeedbackSnackbar, useIframeResizer } from './common';
import { config } from '../../../config';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const ImageChat: React.FC = () => {
  const { t } = useTranslation();
  const [uploadState, setUploadState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useIframeResizer();

  const handleNewChat = () => {
    setMessages([]);
    setCurrentMessage('');
    setSelectedImage(null);
    setIsLoading(false);
    setUploadState({
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setSelectedImage(base64String);
    };
    reader.readAsDataURL(file);
  };

  const captureScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
        },
      });

      // Create a video element to capture the stream
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      // Add a small delay to allow user to prepare
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create a canvas to draw the video frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current frame to the canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      // Convert canvas to base64 image
      const base64Image = canvas.toDataURL('image/jpeg');
      setSelectedImage(base64Image);

      // Stop all tracks
      stream.getTracks().forEach((track) => track.stop());

      // Clean up
      video.srcObject = null;
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        error: t('imageChat.messages.screenshotError'),
      }));
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !selectedImage || isLoading) return;

    setIsLoading(true);
    const newUserMessage: Message = {
      role: 'user',
      content: currentMessage,
    };
    setMessages([...messages, newUserMessage]);
    setCurrentMessage('');

    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gemini-1.5-flash-8b',
          contents: [
            {
              parts: [
                {
                  inlineData: {
                    mimeType: 'image/jpeg',
                    data: selectedImage.split(',')[1], // Remove the data URL prefix
                  },
                },
                {
                  text: currentMessage,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(t('imageChat.messages.apiError'));
      }

      const data = await response.json();
      if (data.candidates && data.candidates[0]) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.candidates[0].content.parts[0].text,
        };
        setMessages([...messages, newUserMessage, assistantMessage]);
      }
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        error: t('imageChat.messages.apiError'),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, fontFamily: 'Inter' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          {messages.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ChatIcon />}
              onClick={handleNewChat}
              sx={{
                boxShadow: 4,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(156, 39, 176, 0.04)',
                },
              }}
            >
              {t('imageChat.buttons.newChat')}
            </Button>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedImage ? (
            <Box
              component="img"
              src={selectedImage}
              alt={t('imageChat.alt.uploadedImage')}
              sx={{
                width: '100%',
                height: 300,
                objectFit: 'contain',
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={6} alignItems="center">
                <Stack alignItems="center" spacing={1}>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                  />
                  <Tooltip title={t('imageChat.tooltips.uploadImage')}>
                    <IconButton
                      color="primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading}
                      size="large"
                    >
                      <ImageIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('imageChat.labels.uploadImage')}
                  </Typography>
                </Stack>
                <Stack alignItems="center" spacing={1}>
                  <Tooltip title={t('imageChat.tooltips.captureScreenshot')}>
                    <IconButton
                      color="primary"
                      onClick={captureScreenshot}
                      disabled={isLoading}
                      size="large"
                    >
                      <ScreenshotMonitorIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('imageChat.labels.takeScreenshot')}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontFamily: 'Inter',
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf:
                    message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '70%',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor:
                      message.role === 'user'
                        ? 'primary.main'
                        : 'background.default',
                    color:
                      message.role === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  {message.role === 'user' ? (
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.content}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        fontFamily: 'Inter',
                        '& p': { m: 0 },
                        '& p:not(:last-child)': { mb: 1 },
                        '& code': {
                          p: 0.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontFamily: 'monospace',
                        },
                        '& pre': {
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          overflow: 'auto',
                          '& code': {
                            p: 0,
                            bgcolor: 'transparent',
                          },
                        },
                        '& ul, & ol': {
                          m: 0,
                          pl: 2.5,
                        },
                        '& li': {
                          mb: 0.5,
                        },
                        '& a': {
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        },
                        '& table': {
                          borderCollapse: 'collapse',
                          width: '100%',
                        },
                        '& th, & td': {
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 1,
                        },
                        '& blockquote': {
                          m: 0,
                          pl: 2,
                          borderLeft: '4px solid',
                          borderColor: 'divider',
                        },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {selectedImage && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                />
                <Tooltip title={t('imageChat.tooltips.uploadImage')}>
                  <IconButton
                    color="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('imageChat.tooltips.captureScreenshot')}>
                  <IconButton
                    color="primary"
                    onClick={captureScreenshot}
                    disabled={isLoading}
                  >
                    <ScreenshotMonitorIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('imageChat.placeholders.askAboutImage')}
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              disabled={!selectedImage || isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                  fontFamily: 'Inter',
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!selectedImage || !currentMessage.trim() || isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Stack>
        </Box>
      </Paper>

      <FeedbackSnackbar
        open={!!uploadState.error}
        message={uploadState.error || t('imageChat.messages.error')}
        severity="error"
        onClose={() => setUploadState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default ImageChat;
